import { useEffect, useRef, useState } from "react";
import { Device, type Call } from "@twilio/voice-sdk";
import { apiFetch, ApiError } from "../lib/api";
import { ErrorBanner } from "../components/EmptyState";

interface TokenResponse {
  token: string;
  identity: string;
  receptionist_number: string;
  test_caller_number: string;
  ttl_seconds: number;
}

type CallState =
  | "idle"
  | "requesting_token"
  | "connecting"
  | "ringing"
  | "in_call"
  | "ended"
  | "error";

export default function VoiceTest() {
  const [state, setState] = useState<CallState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [target, setTarget] = useState<{
    receptionist: string;
    caller: string;
  } | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);

  // Tick the elapsed-seconds counter while in-call.
  useEffect(() => {
    if (state !== "in_call" || callStartedAt == null) return;
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - callStartedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [state, callStartedAt]);

  // Tear down the Device on unmount so we don't leave a registered client behind.
  useEffect(() => {
    return () => {
      try {
        callRef.current?.disconnect();
      } catch {}
      try {
        deviceRef.current?.destroy();
      } catch {}
    };
  }, []);

  async function startCall() {
    setError(null);
    setMuted(false);
    setState("requesting_token");
    let tokenRes: TokenResponse;
    try {
      tokenRes = await apiFetch<TokenResponse>(
        "/api/tenant/voice-test/token",
        { method: "POST", body: "{}" },
      );
    } catch (e) {
      const err = e as ApiError;
      setError(prettyTokenError(err));
      setState("error");
      return;
    }
    setTarget({
      receptionist: tokenRes.receptionist_number,
      caller: tokenRes.test_caller_number,
    });

    try {
      // Tear down any previous device before creating a new one. The SDK
      // requires a fresh Device per token (tokens are short-lived JWTs).
      try {
        deviceRef.current?.destroy();
      } catch {}
      const device = new Device(tokenRes.token, {
        logLevel: 1,
        codecPreferences: ["opus" as any, "pcmu" as any],
      });
      deviceRef.current = device;

      device.on("error", (e: any) => {
        setError(e?.message ?? "Twilio Device error");
        setState("error");
      });

      setState("connecting");
      const call = await device.connect({
        params: { To: tokenRes.receptionist_number },
      });
      callRef.current = call;

      call.on("ringing", () => setState("ringing"));
      call.on("accept", () => {
        setState("in_call");
        setCallStartedAt(Date.now());
      });
      call.on("disconnect", () => {
        setState("ended");
        setCallStartedAt(null);
        callRef.current = null;
      });
      call.on("cancel", () => {
        setState("ended");
        callRef.current = null;
      });
      call.on("reject", () => {
        setState("ended");
        callRef.current = null;
      });
      call.on("error", (e: any) => {
        setError(e?.message ?? "Call error");
        setState("error");
        callRef.current = null;
      });
    } catch (e: any) {
      setError(e?.message ?? "Failed to start call");
      setState("error");
    }
  }

  function hangup() {
    try {
      callRef.current?.disconnect();
    } catch {}
  }

  function toggleMute() {
    if (!callRef.current) return;
    const next = !muted;
    callRef.current.mute(next);
    setMuted(next);
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Voice test</h1>
        <p className="mt-1 text-sm text-slate-500">
          Place a call from your browser to the active business&apos;s
          receptionist number. Audio runs through your laptop&apos;s mic and
          speakers. Calls show up in Conversations marked as <em>test</em>.
        </p>
      </header>

      {error && <ErrorBanner message={error} />}

      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md">
        <CallStatusBlock
          state={state}
          target={target}
          elapsedSec={elapsedSec}
        />

        <div className="mt-6 flex flex-col gap-2">
          {state === "idle" || state === "ended" || state === "error" ? (
            <button
              onClick={startCall}
              className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
            >
              📞 Call receptionist
            </button>
          ) : (
            <>
              <button
                onClick={hangup}
                disabled={state === "requesting_token" || state === "connecting"}
                className="w-full px-4 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                Hang up
              </button>
              <button
                onClick={toggleMute}
                disabled={state !== "in_call"}
                className="w-full px-4 py-2 rounded-xl border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {muted ? "🔇 Unmute" : "🎙 Mute"}
              </button>
            </>
          )}
        </div>
      </div>

      <details className="text-sm text-slate-500">
        <summary className="cursor-pointer hover:text-slate-700">
          How this works
        </summary>
        <div className="mt-3 space-y-2 max-w-2xl">
          <p>
            Browser → Twilio over WebRTC, Twilio dials the receptionist number
            from a dedicated test caller ID. The receptionist picks up via its
            normal /voice/inbound webhook. Cost: ~$0.0225/min combined ($1.15/mo
            for the test number).
          </p>
          <p>
            Calls inbound from the test caller number are tagged with{" "}
            <code className="bg-slate-100 px-1 rounded">is_test: true</code> in
            the conversation metadata so they don&apos;t pollute real metrics.
          </p>
          <p>
            Tokens are minted per-call, valid for 1 hour, capped at 10/hour per
            operator. If you get rate-limited, wait or restart the server.
          </p>
        </div>
      </details>
    </div>
  );
}

function CallStatusBlock({
  state,
  target,
  elapsedSec,
}: {
  state: CallState;
  target: { receptionist: string; caller: string } | null;
  elapsedSec: number;
}) {
  return (
    <div className="text-center">
      <div className="text-6xl mb-3">{stateIcon(state)}</div>
      <div className="text-lg font-semibold text-slate-900">
        {stateLabel(state)}
      </div>
      {target && (
        <div className="mt-3 text-xs text-slate-500 font-mono">
          {target.caller} → {target.receptionist}
        </div>
      )}
      {state === "in_call" && (
        <div className="mt-2 text-2xl font-mono text-slate-700 tabular-nums">
          {formatElapsed(elapsedSec)}
        </div>
      )}
    </div>
  );
}

function stateIcon(state: CallState): string {
  switch (state) {
    case "idle":
      return "📵";
    case "requesting_token":
      return "🔑";
    case "connecting":
      return "📡";
    case "ringing":
      return "📲";
    case "in_call":
      return "🟢";
    case "ended":
      return "📴";
    case "error":
      return "⚠️";
  }
}

function stateLabel(state: CallState): string {
  switch (state) {
    case "idle":
      return "Ready";
    case "requesting_token":
      return "Requesting token…";
    case "connecting":
      return "Connecting…";
    case "ringing":
      return "Ringing…";
    case "in_call":
      return "In call";
    case "ended":
      return "Call ended";
    case "error":
      return "Error";
  }
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function prettyTokenError(err: ApiError): string {
  if (err.status === 503) {
    return "Voice test isn't configured on the server. Set the TWILIO_API_KEY / TWILIO_API_SECRET / TWILIO_TWIML_APP_SID / TWILIO_TEST_CALLER_NUMBER secrets and redeploy.";
  }
  if (err.status === 403) {
    return "Voice test is operator-only. Switch to an operator session.";
  }
  if (err.status === 429) {
    return "Rate limit hit (10 test calls/hour). Wait, or restart the server to clear.";
  }
  if (err.status === 400) {
    return "The active business has no phone_number set. Configure one in Settings first.";
  }
  return err.message ?? "Failed to fetch token.";
}
